import React, { useState } from 'react';
import PropTypes from 'prop-types';
import Popover from 'Components/ComponentLibrary/ExternalLibraries/Libraries/MaterialV4/Popover';
import { noop } from 'Helpers/PropTypes';
import fr from 'facet-rest';
import {
  FDBox, FDHeadlineText, FDSearchInput, FDStack,
} from '@facet-wealth/design';
import useUserContext from 'Context/UserContext';
import { useMessagesContext } from 'Views/Page/Content/Templates/MessagesPage/MessagesContext';
import useSearch from 'Components/UI/ActivityFeed/Shared/useSearch';
import {
  ThreadParticipantList,
} from 'Views/Page/Content/Templates/MessagesPage/components/MessagesPageContent/components/MessagesPanel/components/MessagesPanelContent/components/ThreadHeader/components/ThreadParticipantAvatarGroup/components/ThreadParticipantPopover/components/ThreadParticipantList';
import {
  JoinThreadButton,
} from 'Views/Page/Content/Templates/MessagesPage/components/MessagesPageContent/components/MessagesPanel/components/MessagesPanelContent/components/ThreadHeader/components/ThreadParticipantAvatarGroup/components/ThreadParticipantPopover/components/JoinThreadButton';
import {
  AddLeadPlannerToThreadButton,
} from 'Views/Page/Content/Templates/MessagesPage/components/MessagesPageContent/components/MessagesPanel/components/MessagesPanelContent/components/ThreadHeader/components/ThreadParticipantAvatarGroup/components/ThreadParticipantPopover/components/AddLeadPlannerToThreadButton';
import {
  ThreadParticipantRemovalConfirmationDialog,
} from 'Views/Page/Content/Templates/MessagesPage/components/MessagesPageContent/components/MessagesPanel/components/MessagesPanelContent/components/ThreadHeader/components/ThreadParticipantAvatarGroup/components/ThreadParticipantRemovalConfirmationDialog';
import useConfirmationDialogState from 'Hooks/useConfirmationDialogState';
import { useSnackbarContext } from 'Context/SnackbarContext';
import useMobileLayout from 'Hooks/useMobileLayout';

const ThreadParticipantListPopover = ({
  open,
  anchorEl,
  handleClose,
}) => {
  const { isAgent } = useUserContext();
  const isMobile = useMobileLayout();
  const { selectedThreadResource, refreshAll } = useMessagesContext();
  const [possibleThreadParticipants, setPossibleThreadParticipants] = useState([]);
  const { displayErrorMessage } = useSnackbarContext();
  const [confirmMember, setConfirmMember] = useState(null);

  const {
    searchText,
    setSearchText,
    handleSearchExit,
    handleSearchEnter,
    searchDebounced,
  } = useSearch({
    onSearchExit: () => {
      setPossibleThreadParticipants([]);
    },
    onSearchValueEmpty: () => {
      setPossibleThreadParticipants([]);
    },
    onSearch: async (searchValue) => {
      const threadParticipantCollection = await selectedThreadResource.relationship(fr.relationships.threadParticipants.name).link('related').get({
        params: { search: searchValue, added: false },
      });
      setPossibleThreadParticipants(threadParticipantCollection.resources().list());
    },
  });

  const handleRemoveMember = async (member) => {
    try {
      await member.delete();
      await refreshAll();
      handleClose();
    } catch (e) {
      displayErrorMessage(e, e.errors);
    }
  };

  const {
    dialogOpen: confirmationDialogOpen,
    loading: confirmationDialogLoading,
    handleSubmit: handleConfirmationSubmit,
    handleOpen: handleConfirmationOpen,
    handleClose: handleConfirmationClose,
  } = useConfirmationDialogState({
    onSubmit: handleRemoveMember,
  });

  const searchNotEmpty = searchText !== '';
  const isThreadMember = Boolean(!selectedThreadResource.relationship(fr.relationships.joinThread.name));
  const showSearch = isThreadMember && isAgent;

  return (
    <React.Fragment>
      <Popover
        id={'thread-participant-list-popover'}
        rounded
        open={open}
        anchorEl={anchorEl}
        onClose={(event) => {
          event.stopPropagation();
          handleClose();
        }}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        <FDStack paddingY={4} paddingX={2} direction={'column'} spacing={showSearch ? 3 : 2} flexGrow={1} width={isMobile ? '100%' : '375px'}>
          <FDBox paddingX={2}>
            <FDHeadlineText size={'sm'} value={'Participants'} />
          </FDBox>
          <FDStack direction={'column'} spacing={showSearch ? 2 : 0}>
            <FDBox paddingX={2}>
              {showSearch ? <FDSearchInput
                placeholder={'Add thread member'}
                value={searchText}
                onChange={(event) => {
                  setSearchText(event.target.value);
                  searchDebounced(event.target.value);
                }}
                fullWidth
                onFocus={handleSearchEnter}
                onBlur={handleSearchExit}
              /> : null }
            </FDBox>
            <FDStack spacing={2} direction={'column'}>
              <ThreadParticipantList
                onRemoveClick={(member, event) => {
                  setConfirmMember(member);
                  handleConfirmationOpen(event);
                }}
                handleClose={handleClose}
                isSearching={searchNotEmpty}
                searchedMembers={possibleThreadParticipants}
                existingMembers={selectedThreadResource.attr('threadMembers').resources().list()}
                members={selectedThreadResource.attr('threadMembers').resources().list()}
              />
              {selectedThreadResource?.relationship(fr.relationships.joinThread.name)
                ? (
                  <FDBox paddingX={2}>
                    <JoinThreadButton threadResource={selectedThreadResource} onJoin={handleClose} />
                  </FDBox>
                ) : null}
              {selectedThreadResource?.relationship(fr.relationships.addLeadPlannerToThread.name) ? (
                <FDBox paddingX={2}>
                  <AddLeadPlannerToThreadButton threadResource={selectedThreadResource} onAdd={handleClose} />
                </FDBox>
              ) : null }
            </FDStack>
          </FDStack>
        </FDStack>
      </Popover>
      {confirmationDialogOpen && (
        <ThreadParticipantRemovalConfirmationDialog
          handleConfirm={() => handleConfirmationSubmit(confirmMember)}
          handleClose={handleConfirmationClose}
          handleConfirmationClose={handleConfirmationClose}
          confirmMember={confirmMember}
          loading={confirmationDialogLoading}
        />
      )}
    </React.Fragment>
  );
};

ThreadParticipantListPopover.defaultProps = {
  open: false,
  handleClose: noop,
};

ThreadParticipantListPopover.propTypes = {
  open: PropTypes.bool,
  anchorEl: PropTypes.object.isRequired,
  handleClose: PropTypes.func,
};

export { ThreadParticipantListPopover };
